import {
  ColumnDef,
  getCoreRowModel,
  SortingState,
  useReactTable,
  ColumnFiltersState,
  PaginationState,
  OnChangeFn,
} from '@tanstack/react-table';
import { Spinner, Table } from '@platform/components';
import { formatFilter } from '../util/FormatFilterState';
import { toSnakeCase } from '../util/SnakeCase';
import { useContext, createContext, useState } from 'react';

export const getAdminQueryOptions = (
  sorting?: SortingState,
  columnFilters?: ColumnFiltersState,
  pagination?: PaginationState,
) => {
  const queryOptions: {
    page: number;
    limit: number;
    sort?: string;
    filter?: string;
  } = { page: 0, limit: 20 };

  if (sorting) {
    queryOptions.sort = sorting
      .map((s) => `${toSnakeCase(s.id)}:${s.desc ? 'DESC' : 'ASC'}`)
      .join(',');
  }

  if (columnFilters) {
    queryOptions.filter = formatFilter(columnFilters);
  }

  if (pagination) {
    queryOptions.page = pagination.pageIndex;
    queryOptions.limit = pagination.pageSize;
  }

  return queryOptions;
};

export type AdminQueryOptions = {
  page: number;
  limit: number;
  sort?: string;
  filter?: string;
};

export const AdminTableContext = createContext<{
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  adminQueryOptions: AdminQueryOptions;
}>({
  sorting: [],
  setSorting: () => {},
  columnFilters: [],
  setColumnFilters: () => {},
  pagination: { pageIndex: 0, pageSize: 20 },
  setPagination: () => {},
  adminQueryOptions: { page: 0, limit: 20 },
});

export const AdminTableProvider = ({ children }: { children: React.ReactNode }) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const adminQueryOptions = getAdminQueryOptions(sorting, columnFilters, pagination);

  return (
    <AdminTableContext.Provider
      value={{
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        pagination,
        setPagination,
        adminQueryOptions,
      }}
    >
      {children}
    </AdminTableContext.Provider>
  );
};

interface AdminTableProps<T> {
  data: T[];
  filteredCount: number;
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  enableFiltering?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
}

export const AdminTable = <T,>(props: AdminTableProps<T>) => {
  const {
    data,
    filteredCount,
    columns,
    isLoading = true,
    isError = false,
    isSuccess = false,
    enableFiltering = false,
    enableSorting = true,
    enablePagination = true,
  } = props;
  const { sorting, setSorting, columnFilters, setColumnFilters, pagination, setPagination } =
    useContext(AdminTableContext);

  const adminTable = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    manualPagination: enablePagination,
    onPaginationChange: setPagination,
    rowCount: filteredCount,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
      },
    },
    manualSorting: enableSorting,
    onSortingChange: setSorting,
    manualFiltering: enableFiltering,
    onColumnFiltersChange: setColumnFilters,
  });

  return (
    <>
      {isLoading && (
        <div className='flex justify-center'>
          <Spinner />
        </div>
      )}
      {isError && (
        <div className='flex justify-center'>
          <p className='text-red-500'>Error loading data</p>
        </div>
      )}
      {isSuccess && <Table table={adminTable} />}
    </>
  );
};
